import { useEffect, useRef, useState, useCallback } from 'react';
import { socketService } from '../../../services/socket';
import api from '../../../services/api';
import type { SLDTelemetryPayload } from '../utils/sldTypes';
import { useSLDVisibility } from './useSLDVisibility';

/**
 * Module-level cache: persists SLD telemetry across component unmount/remount
 * (e.g., switching tabs and coming back). Keyed by siteId.
 */
const siteCache = new Map<string, SLDTelemetryPayload>();

/** Track which sites have already been seeded from REST to avoid repeated fetches */
const seededSites = new Set<string>();

/**
 * Subscribes to SLD telemetry via WebSocket when visible.
 *
 * Key behaviours:
 *  - On mount, fetches latest telemetry via REST to populate SLD immediately
 *  - MERGES new device data into accumulated state (never replaces wholesale)
 *  - Caches last-known state per site so tab switches don't blank out the diagram
 *  - Debounces rapid-fire updates during Event Hub backlog replay
 */
export function useSLDTelemetry(
  siteId: string | undefined,
  tabActive: boolean
): SLDTelemetryPayload | null {
  const cached = siteId ? siteCache.get(siteId) ?? null : null;
  const [telemetry, setTelemetry] = useState<SLDTelemetryPayload | null>(cached);
  const isVisible = useSLDVisibility(tabActive);
  const subscribedRef = useRef(false);

  // Accumulated device data — survives rapid events within a single mount
  const accRef = useRef<Record<string, Record<string, number | string | boolean | null>>>(
    cached?.devices ? { ...cached.devices } : {}
  );

  // Debounce timer ref
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset accumulator when siteId changes
  useEffect(() => {
    const existing = siteId ? siteCache.get(siteId) : null;
    accRef.current = existing?.devices ? { ...existing.devices } : {};
    setTelemetry(existing ?? null);
  }, [siteId]);

  const flushState = useCallback((sId: string, timestamp: string) => {
    const payload: SLDTelemetryPayload = {
      siteId: sId,
      timestamp,
      devices: { ...accRef.current },
    };
    siteCache.set(sId, payload);
    setTelemetry(payload);
  }, []);

  // Fetch initial data from REST endpoint on mount (RC-1 fix)
  // This populates the SLD immediately without waiting for WebSocket events.
  useEffect(() => {
    if (!siteId) return;
    // Skip if we already have cached data or already seeded this site
    if (siteCache.has(siteId) || seededSites.has(siteId)) return;

    let cancelled = false;
    // Capture siteId in a const so TypeScript narrows it inside the async closure
    const currentSiteId = siteId;

    async function fetchInitialData() {
      try {
        const response = await api.get(`/v1/sites/${currentSiteId}/sld/latest`);
        if (cancelled) return;

        const data = response.data?.data;
        if (data?.devices && Object.keys(data.devices).length > 0) {
          // Merge fetched data into accumulator
          for (const [deviceId, metrics] of Object.entries(data.devices)) {
            const existing = accRef.current[deviceId];
            accRef.current[deviceId] = existing
              ? { ...existing, ...(metrics as Record<string, number | string | boolean | null>) }
              : { ...(metrics as Record<string, number | string | boolean | null>) };
          }

          seededSites.add(currentSiteId);
          flushState(currentSiteId, data.timestamp || new Date().toISOString());
        }
      } catch (err) {
        // Non-fatal: SLD will still work via WebSocket, just with delayed data
        console.warn('[SLD] Failed to fetch initial telemetry:', err);
      }
    }

    fetchInitialData();

    return () => {
      cancelled = true;
    };
  }, [siteId, flushState]);

  useEffect(() => {
    if (!siteId) return;

    const socket = socketService.getSocket();
    if (!socket) return;

    const handleTelemetry = (payload: SLDTelemetryPayload) => {
      if (payload.siteId !== siteId) return;

      // MERGE new data into accumulator — never throw away old data
      for (const [deviceId, metrics] of Object.entries(payload.devices)) {
        const existing = accRef.current[deviceId];
        accRef.current[deviceId] = existing
          ? { ...existing, ...metrics }
          : { ...metrics };
      }

      // Debounce: during backlog replay the backend fires dozens of events
      // in rapid succession. Flush to React state at most every 500ms.
      if (!flushTimerRef.current) {
        flushTimerRef.current = setTimeout(() => {
          flushTimerRef.current = null;
          flushState(siteId, payload.timestamp);
        }, 500);
      }
    };

    socket.on('sld:telemetry', handleTelemetry);

    return () => {
      socket.off('sld:telemetry', handleTelemetry);
      if (flushTimerRef.current) {
        clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
    };
  }, [siteId, flushState]);

  // Subscribe/unsubscribe based on visibility
  useEffect(() => {
    if (!siteId) return;

    const socket = socketService.getSocket();
    if (!socket) return;

    if (isVisible && !subscribedRef.current) {
      socket.emit('subscribe:sld', { siteId });
      subscribedRef.current = true;
    } else if (!isVisible && subscribedRef.current) {
      socket.emit('unsubscribe:sld', { siteId });
      subscribedRef.current = false;
    }

    return () => {
      if (subscribedRef.current) {
        socket.emit('unsubscribe:sld', { siteId });
        subscribedRef.current = false;
      }
    };
  }, [siteId, isVisible]);

  return telemetry;
}
